import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 *
 * stimulus mode: compact — verb info + conjugation table only
 * support mode: full educational panel with explanations and tips
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label,
  mode = 'support'
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  // ── STIMULUS MODE: compact — verb + table only ──────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-9165">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-id="element-9166">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-9167">
              <span className="text-xs font-bold text-indigo-800" data-id="element-9168">{label}</span>
            </div>}

          {/* Verb Info - compact */}
          <div className="mb-3 flex items-center justify-center gap-3" data-id="element-9169">
            <div className="text-center" data-id="element-9170">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-9171">
                Werkwoord
              </div>
              <div className="text-lg font-black text-indigo-700" data-id="element-9172">{verb}</div>
            </div>
            <div className="text-lg text-slate-300" data-id="element-9173">→</div>
            <div className="text-center" data-id="element-9174">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-9175">
                Stam
              </div>
              <div className="text-lg font-black text-purple-700" data-id="element-9176">{stem}</div>
            </div>
          </div>

          {/* Conjugation Table - compact */}
          <div className="bg-white/90 rounded-lg overflow-hidden border border-indigo-200" data-id="element-9177">
            <table className="w-full text-sm" data-id="element-9178">
              <thead data-id="element-9179">
                <tr className="bg-indigo-100/70" data-id="element-9180">
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-9181">
                    Persoon
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-9182">
                    Vorm
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-9183">
                {persons.map((person, index) => {
                const form = forms[index];
                const isHighlighted = highlight === person || highlight === form;
                return <motion.tr key={person} initial={{
                  opacity: 0,
                  x: -5
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.06
                }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : ''}`} data-id="element-9184">
                      <td className="px-3 py-1.5" data-id="element-9185">
                        <span className={`font-semibold text-xs ${isHighlighted ? 'text-indigo-700' : 'text-slate-600'}`} data-id="element-9186">
                          {person}
                        </span>
                      </td>
                      <td className="px-3 py-1.5" data-id="element-9187">
                        <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-9188">
                          {form}
                        </span>
                      </td>
                    </motion.tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-9189">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-9190">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-9191">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-9192">
            <span className="text-2xl" data-id="element-9193">📝</span>
            <div className="text-left" data-id="element-9194">
              <div className="text-sm font-bold text-indigo-900" data-id="element-9195">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-9196">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-9197">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-9198">
            <div className="text-center" data-id="element-9199">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-9200">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-9201">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-9202">→</div>
            <div className="text-center" data-id="element-9203">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-9204">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-9205">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-9206">
          <table className="w-full" data-id="element-9207">
            <thead data-id="element-9208">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-9209">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-9210">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-9211">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-9212">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-9213">
                    <td className="px-4 py-3" data-id="element-9214">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-9215">
                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-9216">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-9217">
                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-9218">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-9219">
            <div className="flex items-start gap-3" data-id="element-9220">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-9221">
                <span className="text-blue-600 text-lg" data-id="element-9222">🔍</span>
              </div>
              <div data-id="element-9223">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-9224">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-9225">
                  De <strong data-id="element-9226">stam</strong> krijg je door <strong data-id="element-9227">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-9228">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-9229">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-9230">
              <div className="flex items-start gap-3" data-id="element-9231">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-9232">
                  <span className="text-emerald-600 text-lg" data-id="element-9233">📋</span>
                </div>
                <div data-id="element-9234">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-9235">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-9236">
                    <p data-id="element-9237">
                      <strong data-id="element-9238">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-9239">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-9240">
                      <strong data-id="element-9241">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-9242">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-9243">
                      <strong data-id="element-9244">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-9245">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-9246">
                      <strong data-id="element-9247">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-9248">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-9249">
            <div className="flex items-start gap-3" data-id="element-9250">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-9251">
                <span className="text-amber-600 text-lg" data-id="element-9252">💡</span>
              </div>
              <div data-id="element-9253">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-9254">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-9255">
                  Bij <strong data-id="element-9256">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-9257">jij loopt</em> wordt{' '}
                  <em data-id="element-9258">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}